// File: src/pages/DeadPartyReport.jsx
// ✅ UI SAME as your current functional page (WareHouseList-like table + same toolbar)
// ✅ Data/behavior like old AgGrid page:
//    - quick search works like ag-grid quickFilter (searches across ALL base fields, not only visible cols)
//    - pagination (default 15 like ag-grid)
//    - column selection persists AND can read old saved format (array of ag-grid columnDefs objects OR array of keys)
//    - download dropdown hover with small delay (like your class page)
//    - download: PDF / CSV / XLS (same options vibe as old)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

// ---- your project utils/APIs ----
import { _GetList } from "../import/ApiEndPoint/ApiCalling";
import { Dead_party_List } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";

/* ---------------- page scoped css ---------------- */
const injectOnce = () => {
  if (document.getElementById("deadparty-inline-css")) return;
  const style = document.createElement("style");
  style.id = "deadparty-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }
  `;
  document.head.appendChild(style);
};

/* ---------------- helpers ---------------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const get = (o, p, d = "") =>
  p
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), o) ?? d;

const fmtDate = (s) => {
  if (!s) return "";
  if (String(s).includes("T")) return String(s).split("T")[0];
  try {
    return new Date(s).toISOString().split("T")[0];
  } catch {
    return String(s);
  }
};

const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};

const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};

const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};

export default function DeadPartyReport() {
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const hideTimeoutRef = useRef(null);

  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  // ag-grid like quick filter (search across ALL base columns)
  const [search, setSearch] = useState("");

  // pagination like old (default 15)
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);

  const [masterShow, setMasterShow] = useState(false);

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  useEffect(() => injectOnce(), []);

  /* ---------------- columns (same data as old page) ---------------- */
  const baseColumns = useMemo(
    () => [
      {
        key: "partyName",
        header: "PARTY NAME",
        headerName: "Party Name", // for backward compat mapping
        field: "Party.CompanyName",
        get: (r) => get(r, "Party.CompanyName", "-"),
        render: (r) => {
          const days = Number(r?.days || 0);
          return (
            <span
              style={{ color: days > 30 ? "red" : "blue", fontWeight: 600 }}
            >
              {get(r, "Party.CompanyName", "-")}
            </span>
          );
        },
      },
      {
        key: "gst",
        header: "GSTIN",
        headerName: "GSTIN",
        field: "Party.gstNumber",
        get: (r) => get(r, "Party.gstNumber", "-"),
      },
      {
        key: "area",
        header: "AREA",
        headerName: "AREA",
        field: "Party.address1",
        get: (r) =>
          [get(r, "Party.address1", ""), get(r, "Party.address2", "")]
            .filter(Boolean)
            .join(" "),
      },
      {
        key: "pincode",
        header: "PIN CODE",
        headerName: "Pin Code",
        field: "Party.pincode",
        get: (r) => get(r, "Party.pincode", "-"),
      },
      {
        key: "grade",
        header: "GRADE",
        headerName: "Grade",
        field: "Party.category.grade",
        get: (r) => get(r, "Party.category.grade", "-"),
      },
      {
        key: "phone",
        header: "PHONE NUMBER",
        headerName: "Phone Number",
        field: "Party.contactNumber",
        get: (r) => get(r, "Party.contactNumber", "-"),
      },
      {
        key: "salesperson",
        header: "SALES PERSON",
        headerName: "Sales Person",
        field: "Party.created_by.firstName",
        get: (r) => get(r, "Party.created_by.firstName", "-"),
      },
      {
        key: "lastPurchase",
        header: "LAST PURCHASE DATE",
        headerName: "Last Purchase Date",
        field: "purchaseDate",
        get: (r) => fmtDate(r?.purchaseDate),
      },
    ],
    [],
  );

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [baseColumns]);

  /* ---------------- restore columns (supports old ag-grid saved format) ---------------- */
  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => {
    const raw = localStorage.getItem("DeadPartiesReports");

    // Default: all
    let nextKeys = baseColumns.map((c) => c.key);

    try {
      const parsed = raw ? JSON.parse(raw) : null;

      // New format: ["partyName","gst",...]
      if (
        Array.isArray(parsed) &&
        parsed.length &&
        typeof parsed[0] === "string"
      ) {
        const cleaned = parsed.filter((k) => colMap.has(k));
        if (cleaned.length) nextKeys = cleaned;
      }

      // Old format: ag-grid columnDefs objects [{headerName, field, ...}, ...]
      if (
        Array.isArray(parsed) &&
        parsed.length &&
        typeof parsed[0] === "object"
      ) {
        const mapped = [];
        parsed.forEach((def) => {
          const hn = toStr(def?.headerName).trim().toLowerCase();
          const fld = toStr(def?.field).trim().toLowerCase();

          const found = baseColumns.find((c) => {
            const cH = toStr(c.headerName || c.header)
              .trim()
              .toLowerCase();
            const cF = toStr(c.field).trim().toLowerCase();
            return (hn && cH === hn) || (fld && cF === fld);
          });

          if (found) mapped.push(found.key);
        });

        if (mapped.length) nextKeys = mapped;
      }
    } catch {
      // ignore
    }

    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  /* ---------------- fetch data ---------------- */
  const reload = async (id, db) => {
    // permissions
    const inside = CheckPermission("Dead Party");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    const url = `${Dead_party_List}/${id}/${db}`;
    const res = await _GetList(url);
    const list = Array.isArray(res?.Parties) ? res.Parties : [];
    setRows(list);
    setAllRows(list);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
        await reload(user?._id, user?.database);
      } catch (e) {
        console.error("[DeadPartyReport] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load dead party report. Check console for details.",
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------------- visible cols ---------------- */
  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  /* ---------------- ag-grid-like quick filter: search across ALL base columns ---------------- */
  const filteredRows = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    if (!term) return rows;

    // search across ALL baseColumns (like ag-grid quick filter)
    return (rows || []).filter((r) =>
      baseColumns.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, search, baseColumns]);

  /* ---------------- pagination ---------------- */
  const totalPages = Math.max(
    1,
    Math.ceil((filteredRows?.length || 0) / (Number(pageSize) || 15)),
  );
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pageRows = filteredRows.slice(startIndex, endIndex);

  useEffect(() => {
    // if pageSize / filter changes, keep page valid
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, pageSize, filteredRows.length]);

  /* ---------------- downloads: PDF / CSV / XLS ---------------- */
  const buildCSV = (cols, dataRows) => {
    const header = cols
      .map((c) => `"${toStr(c.header).replace(/"/g, '""')}"`)
      .join(",");
    const lines = (dataRows || []).map((r) =>
      cols
        .map((c) => {
          const raw = c.get(r);
          const s = toStr(raw).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(","),
    );
    return [header, ...lines].join("\r\n");
  };

  const handleExportPDF = async () => {
    try {
      const cols = visibleCols;
      const csv = buildCSV(cols, filteredRows);
      await exportDataToPDF(csv, "DeadPartyList");
    } catch (e) {
      console.error(e);
      swal("Unable to export PDF");
    }
  };

  const handleExportCSV = () => {
    try {
      const cols = visibleCols;
      const csv = buildCSV(cols, filteredRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `DeadParty.csv`);
    } catch (e) {
      console.error(e);
      swal("Unable to export CSV");
    }
  };

  const handleExportXLS = () => {
    try {
      const cols = visibleCols;
      const data = filteredRows.map((r) => {
        const obj = {};
        cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
        return obj;
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // .xls like your old menu
      const buf = XLSX.write(wb, { bookType: "xls", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/vnd.ms-excel" }),
        `DeadParty.xls`,
      );
    } catch (e) {
      console.error(e);
      swal("Unable to export XLS");
    }
  };

  /* ---------------- Column chooser modal ---------------- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();

    // Save in a way we can always restore (keys)
    localStorage.setItem("DeadPartiesReports", JSON.stringify(selectedKeys));

    closeModal();
  };

  /* ---------------- Master switcher ---------------- */
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    const raw = localStorage.getItem("SuperadminIdByMaster");
    if (!raw) return;
    const [id, db] = JSON.parse(raw).split(" ");

    setLoading(true);
    setErrorMsg("");
    try {
      await reload(id, db);
      setPage(1);
    } catch (e1) {
      setErrorMsg(
        e1?.response?.data?.message ||
          e1?.message ||
          "Unable to load for selected SuperAdmin.",
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownChange = (selectedValue) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(selectedValue));
  };

  /* ---------------- download hover (like old) ---------------- */
  const onDownloadEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setDownloadOpen(true);
  };

  const onDownloadLeave = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setDownloadOpen(false);
    }, 200);
  };

  /* ---------------- loading full page ---------------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {/* Error banner */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar (keep exactly same UI style) === */}
      <Row className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Dead Party{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              {/* Master switcher */}
              {masterShow ? (
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 240px" }} />
              )}

              {/* Search (quick filter style) */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search parties..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* Page size (ag-grid like) */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="pill-muted">Rows</span>
                <Input
                  type="select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value || 15));
                    setPage(1);
                  }}
                  style={{ width: 90, background: "#fff" }}
                >
                  {[10, 15, 20, 30, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Column filter button */}
              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {/* Download dropdown (hover like old) */}
              {permissions?.Download && (
                <div
                  onMouseEnter={onDownloadEnter}
                  onMouseLeave={onDownloadLeave}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div onClick={handleExportCSV} style={dlItemStyle}>
                        .CSV
                      </div>
                      <div onClick={handleExportXLS} style={dlItemStyle}>
                        .XLS
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table (same UI) === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, idx) => (
                      <tr
                        key={
                          r?._id ||
                          `${get(r, "Party._id", "")}-${startIndex + idx}`
                        }
                      >
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, idx)
                              : toStr(c.get(r, idx))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer (ag-grid like) */}
            {!noData && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  color="secondary"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>

                <span className="pill-muted">
                  Page <b>{safePage}</b> of <b>{totalPages}</b>
                </span>

                <Button
                  color="secondary"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>

                <div className="pill-muted" style={{ marginLeft: "auto" }}>
                  Showing <b>{pageRows.length}</b> of{" "}
                  <b>{filteredRows.length}</b>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal (keep same UI) === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button
                  color="primary"
                  onClick={(e) => {
                    applyColumns(e);
                    setPage(1);
                  }}
                >
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
